import { useCallback, useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';

import { API_PATHS } from 'src/constants/apiPaths';
import { UserDevicesSelectorViewModel } from 'src/types/userDevicesSelector';

const ACCESS_TOKEN_MESSAGE_TYPE = 'AccessToken';
const REQUEST_ACCESS_TOKEN_MESSAGE_TYPE = 'RequestAccessToken';
const MESSAGE_EVENT = 'message';

interface UseUserDevicesOptions {
  baseUrl: string;
}

interface UseUserDevicesResult {
  data: UserDevicesSelectorViewModel;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

async function fetchUserDevices(
  baseUrl: string,
  token: string | null
): Promise<UserDevicesSelectorViewModel> {
  const url = `${baseUrl}${API_PATHS.USER_DEVICES}`;

  const response = await axios.get<UserDevicesSelectorViewModel>(url, {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  return response.data;
}

export function useUserDevices(options: UseUserDevicesOptions): UseUserDevicesResult {
  const { baseUrl } = options;

  const [data, setData] = useState<UserDevicesSelectorViewModel>(
    {} as UserDevicesSelectorViewModel
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === ACCESS_TOKEN_MESSAGE_TYPE && event.data.token) {
        setToken(event.data.token);
      }
    };

    window.addEventListener(MESSAGE_EVENT, handleMessage);

    window.parent?.postMessage({ type: REQUEST_ACCESS_TOKEN_MESSAGE_TYPE }, '*');

    return () => {
      window.removeEventListener(MESSAGE_EVENT, handleMessage);
    };
  }, []);

  const fetchDevices = useCallback(async () => {
    if (!baseUrl) {
      setError(new Error('Devices API base URL is not configured'));
      return;
    }

    if (!token) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const devicesResponse = await fetchUserDevices(baseUrl, token);
      setData(
        devicesResponse && Array.isArray(devicesResponse.devices)
          ? devicesResponse
          : ({} as UserDevicesSelectorViewModel)
      );
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorMessage =
        axiosError.response?.status === 401
          ? 'Authentication failed. Please log in again.'
          : axiosError.message || 'Failed to fetch devices';
      setError(new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl, token]);

  useEffect(() => {
    if (typeof window !== 'undefined' && token) {
      fetchDevices();
    }
  }, [fetchDevices, token]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchDevices,
  };
}
